import "./UserDetailsComponent.scss";
import React, {useCallback, useEffect, useState} from "react";
import {useNavigate, useParams} from "react-router-dom";
import {useDispatch, useSelector} from "react-redux";
import {IRootReducerState} from "../../../store/reducers";
import commonService from "../../../shared/services/common.service";
import {setCurrentNavParams, setSideMenuView} from "../../../store/actions/header.action";
import {CommonService} from "../../../shared/services";
import {ImageConfig} from "../../../constants";
import {IAPIResponseType} from "../../../shared/models/api.model";
import LottieFileGenarationComponent
    from "../../../shared/components/core/lottie-file-genaration/LottieFileGenarationComponent";
import StatusCardComponent from "../../../shared/components/status-card/statusCardComponent";
import HeadingComponent from "../../../shared/components/heading/headingComponent";
import DetailsDataKeyValueComponent
    from "../../../shared/components/details-data-key-value-component/detailsDataKeyValueComponent";
import TabsWrapperComponent, {
    TabComponent,
    TabContentComponent,
    TabsComponent
} from "../../../shared/components/tabs/tabsComponent";
import UserDetailsViewEditComponent from "./user-details-view-edit/UserDetailsViewEditComponent";
import UserPermissionsViewEditComponent from "./user-permissions-view-edit/UserPermissionsViewEditComponent";
import ButtonComponent from "../../../shared/components/core/button/ButtonComponent";
import UserCredentialsTabComponent from "./user-credentials-tab/UserCredentialsTabComponent";

interface UserDetailsComponentProps {

}

const USER_DETAILS_TAB = 'user_details';
const PERMISSIONS = 'permissions';
const USER_CREDENTIALS = 'user_credentials';

const UserDetailsComponent = (props: UserDetailsComponentProps) => {
    const [activeTab, setActiveTab] = useState<"user_details" | "permissions">(USER_DETAILS_TAB);
    const navigate = useNavigate();
    const params = useParams<{ userID: string | undefined }>();
    const {userID} = params;
    const [userDetails, setUserDetails] = useState<any>(undefined);
    const [isUserDetailsLoading, setIsUserDetailsLoading] = useState<boolean>(false);
    const [isUserDetailsLoaded, setIsUserDetailsLoaded] = useState<boolean>(false);
    const [isUserDetailsLoadingFailed, setIsUserDetailsLoadingFailed] = useState<boolean>(false);
    const [userDetailsMode, setUserDetailsMode] = useState<"view" | "edit">("view");
    const {currentUser} = useSelector((state: IRootReducerState) => state.account);
    const [canEdit, setCanEdit] = useState<boolean>(false);
    const [roleDetails, setRoleDetails] = useState<any>({});
    const [isRoleDetailsLoading, setIsRoleDetailsLoading] = useState(false);
    const [isRoleDetailsLoaded, setIsRoleDetailsLoaded] = useState(false);
    const [isRoleDetailsLoadingFailed, setIsRoleDetailsLoadingFailed] = useState(false);
    const [isUserRoleDetailsLoading, setIsUserRoleDetailsLoading] = useState(false);
    const [isUserRoleDetailsLoaded, setIsUserRoleDetailsLoaded] = useState(false);
    const [isUserRoleDetailsLoadingFailed, setIsUserRoleDetailsLoadingFailed] = useState(false);
    const [userRoleDetails, setUserRoleDetails] = useState<any>({});
    const [userDeletePermissions, setUserDeletePermissions] = useState(false);

    const dispatch = useDispatch();

    useEffect(() => {
        setCanEdit(commonService.checkAccess('User',
            'userEdit', currentUser?.userAccessModules))
        setUserDeletePermissions(commonService.checkAccess('User',
            "deleteUser", currentUser?.userAccessModules))
    }, [currentUser]);

    useEffect(() => {
        if (userID) {
            dispatch(setCurrentNavParams("Back", "User", CommonService._routeConfig.UserListRoute()));
            dispatch(setSideMenuView('compact'));
        }
    }, [userID, dispatch, navigate]);

    const handleActiveTabChange = useCallback((value: any) => {
        if (userDetailsMode === 'edit') {
            CommonService.openConfirmationDialog(
                {
                    confirmationTitle: 'Please confirm!',
                    confirmationSubTitle: 'Are you sure you want to discard the recent changes?',
                    disableEscKeyClose: false,
                    disableBackdropClose: true,
                    imagePath: `${ImageConfig.ConfirmationImage}`,
                    direction: "up",
                    yes: {
                        text: "Discard",
                        color: "primary"
                    },
                    no: {
                        text: "Cancel",
                        color: "primary"
                    }
                }).then(() => {
                setActiveTab(value);
            }).catch(() => {

            })
        } else {
            setActiveTab(value);
        }
    }, [userDetailsMode])

    const getUserDetails = useCallback(() => {
        const payload = {};
        let userDetails: any = {};
        setIsUserDetailsLoading(true);
        setIsUserDetailsLoaded(false);
        setIsUserDetailsLoadingFailed(false);
        CommonService._user.getUserDetails(userID, payload)
            .then((response: IAPIResponseType<any>) => {
                if (response?.data) {
                    userDetails = response.data;
                }
                setUserDetails(userDetails);
                setIsUserDetailsLoading(false);
                setIsUserDetailsLoaded(true);
                setIsUserDetailsLoadingFailed(false);
            })
            .catch((error: any) => {
                setUserDetails(userDetails);
                setIsUserDetailsLoading(false);
                setIsUserDetailsLoaded(false);
                setIsUserDetailsLoadingFailed(true);
            });
    }, [userID]);

    const getRoleDetails = useCallback((roleID: any) => {
        const payload = {};
        setIsRoleDetailsLoading(true);
        setIsRoleDetailsLoaded(false);
        setIsRoleDetailsLoadingFailed(false);
        CommonService._accessLevel.getAccessLevelsOfRole(roleID, payload)
            .then((response: any) => {
                setIsRoleDetailsLoading(false);
                setIsRoleDetailsLoaded(true);
                setIsRoleDetailsLoadingFailed(false);
                const data = response?.data?.map((item: any) => {
                    const features = item?.features?.map((featureItem: any) => {
                        return {...featureItem, isDefault: featureItem.isAccessible}
                    })
                    return {...item, features: features, isDefault: item.isAccessible}
                })
                setRoleDetails(data);
            })
            .catch((error: any) => {
                setIsRoleDetailsLoading(false);
                setIsRoleDetailsLoadingFailed(true);
                setIsRoleDetailsLoaded(false);
                setRoleDetails(undefined)
                commonService._alert.showToast(error?.error, "warning");
            })
    }, []);

    useEffect(() => {
        if (userDetails?.roleId) {
            getRoleDetails(userDetails?.roleId);
        }
    }, [userDetails?.roleId, getRoleDetails]);

    const handleUserDetailsUpdate = useCallback((userBasicDetails: any) => {
        setUserDetails(() => {
            return {
                ...userBasicDetails
            }
        });
    }, []);

    const handleRemoveUser = useCallback((userID: string | number) => {
        CommonService.openConfirmationDialog(
            {
                confirmationTitle: 'Please confirm!',
                confirmationSubTitle: 'Are you sure you want to delete the user?',
                disableEscKeyClose: false,
                disableBackdropClose: true,
                imagePath: `${ImageConfig.ConfirmationImage}`,
                direction: "up",
                yes: {
                    text: "Delete",
                    color: "primary"
                },
                no: {
                    text: "Cancel",
                    color: "primary"
                }
            }).then(() => {
            CommonService._user.deleteUser(userID, {}).then((response: IAPIResponseType<any>) => {
                CommonService._alert.showToast("User Successfully Deleted", "success");
                navigate(CommonService._routeConfig.UserListRoute())
            }).catch((error: any) => {
                CommonService._alert.showToast(error?.message || "Error while removing user", "error");
            })
        }).catch(() => {

        })
    }, [navigate])

    useEffect(() => {
        if (userID) {
            getUserDetails();
        }
    }, [userID, getUserDetails]);

    const getUserRoles = useCallback((userId: any) => {
        const payload = {};
        setIsUserRoleDetailsLoading(true);
        setIsUserRoleDetailsLoaded(false);
        setIsUserRoleDetailsLoadingFailed(false);
        CommonService._user.getUserRoles(userId, payload)
            .then((response: any) => {
                setIsUserRoleDetailsLoading(false);
                setIsUserRoleDetailsLoaded(true);
                setIsUserRoleDetailsLoadingFailed(false);
                const data = response?.data?.map((item: any, index: number) => {
                    const features = item?.features?.map((featureItem: any, fetureIndex: number) => {
                        return {...featureItem, isDefault: roleDetails[index]?.features[fetureIndex]?.isAccessible}
                    })
                    return {...item, features: features, isDefault: roleDetails[index]?.isAccessible}
                })
                setUserRoleDetails(data);
            })
            .catch((error: any) => {
                commonService._alert.showToast(error?.error, "warning");
                setIsUserRoleDetailsLoading(false);
                setIsUserRoleDetailsLoaded(false);
                setIsUserRoleDetailsLoadingFailed(true);
            })

    }, [roleDetails]);

    useEffect(() => {
        if (userID) {
            getUserRoles(userID);
        }
    }, [userID, getUserRoles]);

    const onDetailsUpdate = useCallback((userDetails: any) => {
        setUserDetails({...userDetails, isEditable: true})
    }, [])

    return (
        <div className={'user-details-component  details-screen screen'}>
            {
                isUserDetailsLoading && <div className="h-v-center">
                    <LottieFileGenarationComponent
                        loop={true}
                        autoplay={true}
                        animationData={ImageConfig.LoadingLottieFile}/>
                </div>
            }
            {
                isUserDetailsLoadingFailed && <>
                    <StatusCardComponent title={"User details failed to load"}/>
                </>
            }
            {
                isUserDetailsLoaded && <>
                    <div className={'details-module-title'}>User Details</div>
                    <div className="basic-details-wrapper">
                        <div className={"details-screen-header-status-section"}>
                            <div className="details-screen-header">
                                <HeadingComponent
                                    title={`${commonService.capitalizeFirstLetterOfEachWord(userDetails?.firstName)} ${userDetails?.middleName === null ? "" : commonService.capitalizeFirstLetterOfEachWord(userDetails?.middleName)} ${commonService.capitalizeFirstLetterOfEachWord(userDetails?.lastName)}`}
                                />
                                {
                                    userDeletePermissions && <ButtonComponent
                                        variant="contained"
                                        color={'primary'}
                                        handleClick={() => {
                                            userID && handleRemoveUser(userID)
                                        }}
                                        disabled={!userDetails?.isEditable}
                                    >
                                        Delete User
                                    </ButtonComponent>
                                }
                            </div>
                        </div>
                        <div className="details-screen-sub-header">
                            <DetailsDataKeyValueComponent
                                title={"Role:"}
                                isHorizontal={true}> {userDetails?.roleObj?.title || userDetails?.roleObj?.role || '-'} </DetailsDataKeyValueComponent>
                            <DetailsDataKeyValueComponent
                                title={"User ID:"}
                                isHorizontal={true}> {userDetails?.id} </DetailsDataKeyValueComponent>
                            <DetailsDataKeyValueComponent
                                title={"Added on:"}
                                isHorizontal={true}>
                                <>
                                    {commonService.formatDate(userDetails?.createdAt)}
                                    &nbsp;|&nbsp;
                                    {commonService.formatTime(userDetails?.createdAt)}
                                </>
                            </DetailsDataKeyValueComponent>

                        </div>

                    </div>
                    <div className="clear-fix"/>
                    <div className="details-screen-body">
                        <TabsWrapperComponent>
                            <TabsComponent
                                value={activeTab}
                                allowScrollButtonsMobile={false}
                                onUpdate={(e: any, v: any) => {
                                    handleActiveTabChange(v)
                                }}
                                variant={"fullWidth"}
                            >
                                <TabComponent label="USER DETAILS" value={USER_DETAILS_TAB}/>
                                <TabComponent label="PERMISSIONS" value={PERMISSIONS}/>
                                <TabComponent label="USER CREDENTIALS" value={USER_CREDENTIALS}/>
                            </TabsComponent>
                            <TabContentComponent selectedTab={activeTab} value={USER_DETAILS_TAB}>
                                <UserDetailsViewEditComponent
                                    onUpdateUserInformation={handleUserDetailsUpdate}
                                    canEdit={canEdit}
                                    userDetails={userDetails}
                                    onModeChange={(mode: any) => {
                                        setUserDetailsMode(mode);
                                    }
                                    }/>
                            </TabContentComponent>
                            <TabContentComponent selectedTab={activeTab} value={PERMISSIONS}>
                                {
                                    (isRoleDetailsLoading || isUserRoleDetailsLoading) && <div className="h-v-center">
                                        <LottieFileGenarationComponent
                                            loop={true}
                                            autoplay={true}
                                            animationData={ImageConfig.LoadingLottieFile}/>
                                    </div>
                                }
                                {
                                    (isRoleDetailsLoadingFailed || isUserRoleDetailsLoadingFailed) && <>
                                        <StatusCardComponent title={"Role details failed to load"}/>
                                    </>
                                }
                                {
                                    isRoleDetailsLoaded && isUserRoleDetailsLoaded && <>
                                        <UserPermissionsViewEditComponent
                                            userID={userID}
                                            canEdit={canEdit}
                                            onModeChange={(mode: any) => {
                                                setUserDetailsMode(mode);
                                            }
                                            }
                                            initialUserRoleDetails={userRoleDetails}
                                            initialRoleDetails={roleDetails}
                                            roleID={userDetails?.roleId}
                                            isHybrid={userDetails?.isHybrid}
                                            isEditable={userDetails?.isEditable}
                                            onDetailsUpdate={onDetailsUpdate}
                                        />
                                    </>
                                }

                            </TabContentComponent>
                            <TabContentComponent value={activeTab} selectedTab={USER_CREDENTIALS}>
                                <UserCredentialsTabComponent userDetails={userDetails} isLoading={isUserDetailsLoading}
                                                             isLoaded={isUserDetailsLoaded}
                                                             isFailed={isUserDetailsLoadingFailed}/>
                            </TabContentComponent>
                        </TabsWrapperComponent>
                    </div>
                </>
            }
        </div>
    );

};

export default UserDetailsComponent;
